import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Invoice.css';
import { FaHospital, FaUser, FaPhone, FaIdCard, FaFlask } from "react-icons/fa";
import {
  User,
  ClipboardSignature,
  Cake,
  UserCircle,
  MapPin,
  Phone,
  CalendarDays,
  UserCheck,
  FlaskConical,
  Barcode,
  ArrowUpFromLine,
   ArrowDownFromLine,
   Minus,
  FileText,
  CheckCircle,
  Ruler,
  AlertTriangle
} from 'lucide-react'
import {
  XCircle,
  CalendarClock,
} from 'lucide-react';
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const LabResultwithQRcode = () => {
  const [invalidData, setInvalidData] = useState(false);
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId");
  const invoiceNumber = searchParams.get("invoice_number");
  const token = searchParams.get("token");
   const [labResults, setLabResults] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const navigate = useNavigate();
  const currentDate = new Date().toLocaleString();
  const [systemData, setSystemData] = useState({
    phoneNumber: '',
    tinNumber: '',
    address: '',
    email: '',
    titleAmharic: '',
    titleEnglish: '',
  });
  
  useEffect(() => {
  // Fetch the current settings from the backend
  const fetchSettings = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/setting/get-settings`);
    setSystemData(response.data);
  } catch (error) {
    toast.error("There is no setting data.");
    console.error(error);
  }
  };
  
  fetchSettings();
  }, []);

  useEffect(() => {
    if (!token || !invoiceNumber || !patientId) {
      setInvalidData(true);
      setLoading(false);
      return;
    }


    const fetchResults = async () => {

      try {
        const response = await axios.get(`${BASE_URL}/labratory/qrcode-lab-results`,
            {
            params: {
              invoiceNumber,
              patient_id: patientId,
              token, 
            }
          }
        ); 
        if (!response.data || response.data.length === 0) {
          setInvalidData(true);
        } else {
        const data = response.data.map(test => {
          let flag = '';
          const match = test.range_value?.match(/([0-9.]+)\s*-\s*([0-9.]+)/);
          if (match) {
            const min = parseFloat(match[1]);
            const max = parseFloat(match[2]);
            const value = parseFloat(test.lab_result);
            if (!isNaN(value)) {
              if (value < min) flag = 'L';
              else if (value > max) flag = 'H';
            }
          }
          return { ...test, flag };
        });
        setLabResults(data);
    }
        if (response.data.length > 0) {
          const patient = response.data[0];
          setPatientDetails({
            patientName: patient.patientName,
            calculated_age: patient.age,
            sex: patient.sex,
            kebele: patient.kebele,
            cardNumber: patient.cardNumber,
            physicianName: patient.physicianName,
          });
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching lab data:", error);
        setInvalidData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [invoiceNumber, patientId, token]);

  const getFlagIcon = (flag) => {
  if (flag === 'H') return <ArrowUpFromLine className="text-red-600 w-4 h-4 font-bold" />;
  if (flag === 'L') return <ArrowDownFromLine className="text-blue-600 w-4 h-4 font-bold" />;
  return <Minus className="text-gray-400 w-4 h-4" />;
};

  const getFlagColor = (flag) => {
    if (flag === 'H') return 'text-red-600 font-bold';
    if (flag === 'L') return 'text-blue-600 font-bold';
    return '';
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;
  if (!labResults) return <p>No lab results found.</p>;

  // Group results by Lab Technician
  const groupedByTechnician = labResults.reduce((acc, curr) => {
    const technician = curr.labTechnicianName;
    if (!acc[technician]) {
       acc[technician] = { tests: {},
        comment: curr.lab_personnel_comment,
        type_of_specimen: curr.type_of_specimen,
        approved_by: curr.approved_by,
        checked_by: curr.checked_by,
        lab_serial_number: curr.lab_serial_number,
        sample_collected_by: curr.sample_collected_by,
        specimen_login_date: curr.specimen_login_date,
        specimen_logout_date: curr.specimen_logout_date,
       };
    }
    if (!acc[technician].tests[curr.category_name]) {
       acc[technician].tests[curr.category_name] = [];
    }
    acc[technician].tests[curr.category_name].push({
       test_name: curr.test_name,
       lab_result: curr.lab_result,
       range_value: curr.range_value,
       lab_item_name: curr.lab_item_name,
       flag: curr.flag,
    });
    return acc;
 }, {});
// 🚨 Invalid Data UI
    if (invalidData) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-green-50">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center p-8 rounded-2xl shadow-lg bg-white border border-green-300"
          >
            <motion.div 
              animate={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
              className="flex justify-center"
            >
              <XCircle className="w-20 h-20 text-green-600" />
            </motion.div>
            <h2 className="mt-4 text-2xl font-bold text-green-700">Invalid Data</h2>
            <p className="text-green-600 mt-2">The medication order for this invoice could not be found.</p>
          </motion.div>
        </div>
      );
    }

  return (
    <div className="min-h-[80vh] w-full md:min-h-screen bg-gray-100 mt-24 px-2 sm:px-4">
     <ToastContainer />
     <div className="flex-grow text-right">
      <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 hidden-print"
      >
          Print
      </button>
      </div>
      <div className="max-w-8xl mx-auto bg-white border border-black rounded-lg shadow-md print-invoice">
       {/* Logo and Header Section */}
        <div className="text-center mb-4">
        <img src="/assets/hiywot_logo.jpg" alt="hiywot_logo" className="mx-auto h-24" />
        <div className="text-lg">
          <p className="text-base font-bold text-gray-800">
            <span className="font-semibold text-blue-600">Email:</span> {systemData.email} |
            
            <span className="font-bold text-blue-600">
              TIN <span className="underline">No</span>:
            </span>  {systemData.tinNumber} |

            <span className="font-bold text-blue-600">
            Phone <span className="underline">No</span> :
              </span> {systemData.phoneNumber} |
            <span className="font-bold text-blue-600"> Address:</span> {systemData.address}
          </p>
        </div>
      </div>

    {/* Laboratory Report Title */}
<div className="flex justify-between items-center">
  <h2 className="flex items-center gap-2 font-bold text-lg text-gray-800">
    <FlaskConical className="w-4 h-4 text-purple-600" />
    Laboratory Report
  </h2>
   <div className="flex items-center gap-2 text-sm text-gray-700">
    <CalendarDays className="w-4 h-4 text-green-600" />
    <span>Date:</span> <span className="underline">{currentDate}</span>
  </div>
</div>
    <div className="grid grid-cols-2 gap-x-2 bg-white">
  {/* Patient Details */}
  <div>
    {patientDetails ? (
      <div className="space-y-2">
        {/* First Row */}
      <div className="flex flex-wrap items-center gap-1">
        <p className="flex items-center gap-1 whitespace-nowrap">
          <User className="w-4 h-4 text-indigo-600" />
          <strong>Client:</strong> <span className="ml-1">{patientDetails.patientName}</span>
        </p>
          <p className="flex items-center gap-1">
          <ClipboardSignature className="w-4 h-4 text-blue-600" />
          <strong>MRN:</strong> <span className="ml-1">{patientDetails.cardNumber}</span>
        </p>
          <p className="flex items-center gap-1">
          <Cake className="w-4 h-4 text-pink-600" />
          <strong>Age:</strong> <span className="ml-1">{patientDetails.calculated_age}</span>
        </p>
        <p className="flex items-center gap-1">
          <UserCircle className="w-4 h-4 text-red-500" />
          <strong>Sex:</strong> <span className="ml-1">{patientDetails.sex}</span>
        </p>
      </div>
      </div>
    ) : (
      <p className="text-gray-600">Loading patient details...</p>
    )}
  </div>
  
  {/* Ordered By */}
  {/* Ordered By */}
{patientDetails?.physicianName && (
  <div className="flex justify-start md:justify-end items-center">
    <p className="flex items-center gap-2 text-xs sm:text-sm">
      <UserCheck className="w-4 h-4 text-teal-600" />
      <strong>Ordered By:</strong> <span>{patientDetails.physicianName}</span>
    </p>
  </div>
)}
</div>
        <div className="overflow-x-auto bg-white rounded shadow text-sm">
        {/* Lab Test Results */}
        <div className="bg-white rounded shadow">
               {Object.entries(groupedByTechnician).map(([technician, data]) => (
                <div key={technician} className="border px-4 rounded shadow">
                <h3 className="font-semibold flex items-center">
                  <FaFlask className="text-blue-600" />Lab Technician: {technician}
                </h3>
                     {Object.entries(data.tests).map(([category, tests]) => (
                        <div key={category} className="">
                           <h3 className="font-semibold">{category}</h3>
                           <table className="table-auto w-full border-collapse border border-gray-500">
                              <thead>
                                 <tr className="bg-gray-400">
                                    <th className="px-1 py-1 text-left">#</th>
                                    <th className="px-2 py-2 text-left font-medium">
                                    <div className="flex items-center gap-1">
                                      <Barcode className="w-4 h-4 text-indigo-600" />
                                      Code
                                    </div>
                                    </th>
                                    <th className="px-2 py-2 text-left font-medium">
                                    <div className="flex items-center gap-1">
                                      <FileText className="w-4 h-4 text-blue-600" />
                                      Description
                                    </div>
                                  </th>
                                    <th className="px-2 py-2 text-left font-medium">
                                    <div className="flex items-center gap-1">
                                      <CheckCircle className="w-4 h-4 text-green-600" />
                                      Result
                                    </div>
                                  </th>
                                  <th className="px-2 py-2 text-left font-medium">
                                  <div className="flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4 text-red-600" />
                                    Flag
                                  </div>
                                </th>
                                    <th className="px-2 py-2 text-left font-medium">
                                <div className="flex items-center gap-1">
                                  <Ruler className="w-4 h-4 text-orange-500" />
                                  Reference Range
                                </div>
                              </th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {tests.map((test, index) => (
                                    <tr key={index} className="hover:bg-gray-300">
                                       <td className="border border-gray-500 px-1 py-1 text-left">{index + 1}</td>
                                       <td className={`border border-gray-500 px-1 py-1 text-left ${getFlagColor(test.flag)}`}>{test.test_name}</td>
                                       <td className={`border border-gray-500 px-1 py-1 text-left ${getFlagColor(test.flag)}`}>{test.lab_item_name}</td>
                                       <td className={`border border-gray-500 px-1 py-1 text-left ${getFlagColor(test.flag)}`}>{test.lab_result}</td>
                                       <td className="border border-gray-500 px-1 py-1 text-left">
                                        <div className="flex items-center gap-1">
                                          {getFlagIcon(test.flag)}
                                          <span className={getFlagColor(test.flag)}>{test.flag}</span>
                                        </div>
                                      </td>
                                       <td className="border border-gray-500 px-1 py-1 text-left">{test.range_value}</td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     ))}
                     <div>
                     <div className="">
                     {/* First Row */}
                     <div className="flex flex-wrap space-x-6">
                       <p className="flex items-center space-x-2">
                         <p>Specimen Type:</p> {data.type_of_specimen}
                       </p>
                       <p className="flex items-center space-x-2">
                         <p>Lab Serial Number:</p> {data.lab_serial_number}
                       </p>
                       <p className="flex items-center space-x-2">
                         <p>Specimen Login Time:</p>{" "} 
                         {data?.specimen_login_date 
                           ? new Date(data.specimen_login_date).toString() !== "Invalid Date"
                             ? new Date(data.specimen_login_date).toLocaleString("en-US", { 
                                 year: 'numeric', 
                                 month: '2-digit', 
                                 day: '2-digit', 
                                 hour: '2-digit', 
                                 minute: '2-digit', 
                               })
                             : " "
                           : " "}
                       </p>
                       <p className="flex items-center space-x-2">
                         <p>Specimen Logout Time:</p>{" "} 
                         {data?.specimen_logout_date 
                           ? new Date(data.specimen_logout_date).toString() !== "Invalid Date"
                             ? new Date(data.specimen_logout_date).toLocaleString("en-US", { 
                                 year: 'numeric', 
                                 month: '2-digit', 
                                 day: '2-digit', 
                                 hour: '2-digit', 
                                 minute: '2-digit', 
                                 hour12: true 
                               })
                             : " "
                           : " "}
                       </p>
                     </div>
                     {/* Second Row */}
                     <div className="flex flex-wrap space-x-6">
                       <p className="flex items-center space-x-2">
                         <p>Sample Collected By:</p> {data.sample_collected_by || ""}
                       </p>
                       <p className="flex items-center space-x-2">
                         <p>Checked By:</p> {data.checked_by || ""}
                       </p>
                       <p className="flex items-center space-x-2">
                         <p>Approved By:</p> {data.approved_by || ""}
                       </p>
                     </div>
                   </div>
                  </div>
                     <div className="px-1 border-t border-gray-400">
                        <p>Comment:</p> {data.comment}
                     </div>
                  </div>
               ))}
            </div>
      </div>
      <div className="flex items-center justify-between py-2">
      <div className="h-[10%] w-[10%]">
      <QRCodeSVG 
        value={`https://asratmediumclinic.com/lab-results?invoice_number=${invoiceNumber}&patientId=${patientId}&token=${token}`} 
        size={128}
        />
        </div>
        </div> 
    </div>
    </div>
  );
};
export default LabResultwithQRcode;
